import type { JSX, ReactNode } from 'react';
import { createContext, useContext } from 'react';

import type { PrepaidApi } from './api';

/**
 * What the §6 prepaid screens need from outside React.
 *
 * The three capabilities are §5's three gates, and they are genuinely
 * different people: `canDefine` writes the price list (`catalogue.write`),
 * `canSell` takes money against a promise (`billing.issue`), and `canVoid`
 * gives it back or corrects a ledger (`billing.void`). A front desk holds
 * the middle one and neither of the others.
 *
 * `outletId` is injected because a sale is booked to an outlet and the
 * feature has no business knowing how an app decides which one.
 */
export interface PrepaidDeps {
  api: PrepaidApi;
  /** `catalogue.write` — define a package or a plan. */
  canDefine: boolean;
  /** `billing.issue` — sell one, or take a top-up. */
  canSell: boolean;
  /** `billing.void` — refund, cancel, correct. */
  canVoid: boolean;
  /** Where a sale is booked. */
  outletId: string;
}

const PrepaidContext = createContext<PrepaidDeps | null>(null);

/** Provide the prepaid dependencies to a subtree. */
export function PrepaidProvider({
  deps,
  children,
}: {
  deps: PrepaidDeps;
  children: ReactNode;
}): JSX.Element {
  return <PrepaidContext.Provider value={deps}>{children}</PrepaidContext.Provider>;
}

/**
 * Read them back.
 *
 * Throws rather than defaulting: a screen rendered outside the provider
 * would otherwise show "you may not do this" to an owner.
 */
export function usePrepaidDeps(): PrepaidDeps {
  const deps = useContext(PrepaidContext);
  if (deps === null) {
    throw new Error('usePrepaidDeps must be used inside a <PrepaidProvider>');
  }
  return deps;
}
